import * as React from "react"
import { Box, Typography, Alert, keyframes, IconButton, Tooltip } from "@mui/material"
import { DrawerSeeker } from "@ui-seeker/core"
import { IconSeeker } from "@ui-seeker/core"

import { useAiInsights } from "@hooks"
import AiInsightsContent from "./Content"


const sparklePulse = keyframes`
	0%, 100% { transform: scale(1); opacity: 0.7; }
	50% { transform: scale(1.25); opacity: 1; }
`

const shimmer = keyframes`
	0% { background-position: -200% 0; }
	100% { background-position: 200% 0; }
`

const skeletonLine = {
	height: 12,
	borderRadius: 6,
	background: 'linear-gradient(90deg, #F3F0FF 25%, #E9DFFC 50%, #F3F0FF 75%)',
	backgroundSize: '200% 100%',
	animation: `${shimmer} 1.8s ease-in-out infinite`,
}


function SkeletonLoading() {
	return (
		<Box sx={{ py: 3, px: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
				<IconSeeker
					icon="wand-magic-sparkles"
					style={{ fontSize: 22, color: '#7F56D9', animation: `${sparklePulse} 2s ease-in-out infinite` }}
				/>
				<Typography variant="body2" sx={{ color: '#7F56D9', fontWeight: 500 }}>
					Analyzing your data…
				</Typography>
			</Box>
			<Box sx={{ ...skeletonLine, width: '90%' }} />
			<Box sx={{ ...skeletonLine, width: '75%' }} />
			<Box sx={{ ...skeletonLine, width: '85%' }} />
			<Box sx={{ ...skeletonLine, width: '60%', mt: 1 }} />
			<Box sx={{ ...skeletonLine, width: '80%' }} />
			<Box sx={{ ...skeletonLine, width: '45%' }} />
		</Box>
	)
}


function EmptyState() {
	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			height: '60%',
			gap: 2,
			color: 'text.secondary',
		}}>
			<IconSeeker icon="wand-magic-sparkles" style={{ fontSize: 40, color: '#D6BBFB' }} />
			<Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 280 }}>
				Select data from any table and use the AI Insights button to generate analysis.
			</Typography>
		</Box>
	)
}


function CopyButton({ text }) {
	const [copied, setCopied] = React.useState(false)

	const handleCopy = React.useCallback(() => {
		navigator.clipboard.writeText(text).then(() => {
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		})
	}, [text])

	return (
		<Tooltip title={copied ? "Copied!" : "Copy to clipboard"} placement="left">
			<IconButton onClick={handleCopy} size="small" sx={{
				color: copied ? '#027A48' : '#98A2B3',
				'&:hover': { color: '#7F56D9', bgcolor: '#F9F5FF' },
			}}>
				<IconSeeker icon={copied ? "check" : "copy"} style={{ fontSize: 16 }} />
			</IconButton>
		</Tooltip>
	)
}


export default function AiInsightsDrawer() {
	const { isOpen, isAnalyzing, text, error, close } = useAiInsights()
	const hasContent = !!text || isAnalyzing || !!error

	return <DrawerSeeker
		width="45%"
		isOpen={isOpen}
		controller={close}
		title="AI Insights"
		hideActions
	>
		<Box sx={{ px: 3, py: 2, height: '100%', overflow: 'auto' }}>
			{text && !isAnalyzing && (
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
					<CopyButton text={text} />
				</Box>
			)}

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{isAnalyzing && !text && <SkeletonLoading />}

			{text && <AiInsightsContent text={text} isStreaming={isAnalyzing} />}

			{!hasContent && <EmptyState />}
		</Box>
	</DrawerSeeker>
}
